import axios from "axios";
import APIURL from "../../../Main/apiurl";

const getAuthToken = () => {
  const token = localStorage.getItem("user");
  return token || null;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  if (!token) return null;

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

const get_all_manual_journals = async (data) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error("Authorization headers missing");
    }


    const { page, limit = 10, search, status, startDate, endDate, sort, source } = data;

    const params = {
      page: page ? page : 1,
      limit,
      ...(source && { source }),
      ...(sort && { sort }),
      ...(search && { search }),
      ...(status && { status }),
      ...(startDate && { startDate: new Date(startDate).toISOString() }),
      ...(endDate && { endDate: new Date(endDate).toISOString() }),
    };

    const response = await axios.get(`${APIURL}/get-all-manual-journals`, {
      headers,
      params,
      paramsSerializer: (params) => {
        return Object.entries(params)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");
      },
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Invalid response structure");
    }

    return response?.data;
  } catch (error) {
    console.error("Error fetching journals:", {
      message: error.message,
      response: error.response?.data,
      config: error.config,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch journal entries"
    );
  }
};

const create_manual_journal = async (journalData) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) throw new Error("Authorization headers missing");

    if (!journalData.referenceNumber) {
      throw new Error("Reference number is missing in the final payload");
    }
    const payload = {
      ...journalData,
      date: journalData.date
        ? new Date(journalData.date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      referenceNumber: journalData.referenceNumber
        .toString()
        .trim()
        .toUpperCase(),
      entries: journalData.entries.map((entry) => ({
        ...entry,
        amount: parseFloat(entry.amount).toFixed(2),
        reference: entry.reference?.trim() || undefined,
        description: entry.description?.trim() || undefined,
      })),
    };
    const response = await axios.post(
      `${APIURL}/create-manual-journal`,
      payload,
      {
        headers,
        timeout: 10000,
      }
    );

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Journal creation failed");
    }

    return response.data;
  } catch (error) {
    console.error("API Error Details:", {
      error: error.message,
      response: error.response?.data,
      sentPayload: journalData,
    });
    throw error;
  }
};

const get_manual_journal = async (id) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) throw new Error("Authorization headers missing");

    const response = await axios.get(`${APIURL}/manual-journal/${id}`, {
      headers,
      timeout: 5000,
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Journal not found");
    }

    return response.data.data || response.data;
  } catch (error) {
    console.error("Error fetching journal:", {
      id,
      error: error.message,
      response: error.response?.data,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch journal entry"
    );
  }
};

const update_manual_journal = async (id, journalData) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) throw new Error("Authorization headers missing");

    const payload = {
      ...journalData,
      date: journalData.date ? new Date(journalData.date) : undefined,
      referenceNumber: journalData.referenceNumber
        ?.toString()
        .trim()
        .toUpperCase(),
      entries: journalData.entries?.map((entry) => ({
        account: entry.account,
        amount: parseFloat(entry.amount).toFixed(2),
        type: entry.type,
        reference: entry.reference?.trim() || undefined,
        description: entry.description?.trim() || undefined,
      })),
    };

    const response = await axios.put(
      `${APIURL}/update-manual-journal/${id}`,
      payload,
      {
        headers,
        timeout: 10000,
      }
    );

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Update failed");
    }

    return response.data.data || response.data;
  } catch (error) {
    console.error("Update error:", {
      id,
      error: error.message,
      response: error.response?.data,
    });
    throw new Error(
      error.response?.data?.message || "Failed to update journal"
    );
  }
};

const delete_manual_journal = async (id) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) throw new Error("Authorization headers missing");

    const response = await axios.delete(`${APIURL}/manual-journal/${id}`, {
      headers,
      timeout: 5000,
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Deletion failed");
    }

    return response.data.data || response.data;
  } catch (error) {
    console.error("Deletion error:", {
      id,
      error: error.message,
      response: error.response?.data,
    });
    throw new Error(
      error.response?.data?.message || "Failed to delete journal"
    );
  }
};

const create_contra_entry = async (contraData) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) throw new Error("Authorization headers missing");

    if (
      !contraData.arAccountId ||
      !contraData.apAccountId ||
      !contraData.amount
    ) {
      throw new Error(
        "Missing required fields: arAccountId, apAccountId, or amount"
      );
    }

    const payload = {
      arAccountId: contraData.arAccountId,
      apAccountId: contraData.apAccountId,
      amount: parseFloat(contraData.amount).toFixed(2),
      description: contraData.description?.trim() || "Contra Entry",
    };

    const response = await axios.post(`${APIURL}/contra-entry`, payload, {
      headers,
      timeout: 10000,
    });

    if (!response.data?.success) {
      throw new Error(
        response.data?.message || "Failed to create contra entry"
      );
    }

    return response.data.data || response.data;
  } catch (error) {
    console.error("Error creating contra entry:", {
      error: error.message,
      response: error.response?.data,
      sentPayload: contraData,
    });
    throw new Error(
      error.response?.data?.message || "Failed to create contra entry"
    );
  }
};

const get_contra_balance = async (contactId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) throw new Error("Authorization headers missing");

    if (!contactId) {
      throw new Error("Contact ID is required");
    }

    const response = await axios.get(`${APIURL}/contra-balance/${contactId}`, {
      headers,
      timeout: 5000,
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Failed to fetch net balance");
    }

    return response.data.data || response.data;
  } catch (error) {
    console.error("Error fetching contra balance:", {
      contactId,
      error: error.message,
      response: error.response?.data,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch net balance"
    );
  }
};

const get_all_combinations = async (data) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) {
      throw new Error("Authorization headers missing");
    }

    const { isContra } = data;

    const params = {
      isContra,
    };

    const response = await axios.get(`${APIURL}/get_All_Allowed_Combinations`, {
      headers,
      params,
      paramsSerializer: (params) => {
        return Object.entries(params)
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join("&");
      },
    });

    if (!response.data?.success) {
      throw new Error(response.data?.message || "Invalid response structure");
    }

    return response?.data;
  } catch (error) {
    console.error("Error fetching journals:", {
      message: error.message,
      response: error.response?.data,
      config: error.config,
    });
    throw new Error(
      error.response?.data?.message || "Failed to fetch journal entries"
    );
  }
};

export {
  create_manual_journal,
  get_all_manual_journals,
  get_manual_journal,
  update_manual_journal,
  delete_manual_journal,
  create_contra_entry,
  get_contra_balance,
  get_all_combinations,
};
